/* eslint-disable max-len */
import { FC, useState, ChangeEvent, useEffect } from "react";

import { Modal, Table, Checkbox, Input } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { maxLengthText } from "common/shared/constants/validationConstants";
import { formatCurrencyWithoutFractional } from "common/shared/helpers";
import { CircleX } from "common/lib/components/Icons";
import { URLs } from "common/lib/constants";
import { Button, Spinner } from "common/lib/components";
import { LiquidityRequestStatus, TAssetType, TConfirmAssetToIncludeItem } from "domains/liquidity/shared/types";
import { setSelectedAssetsForLiquidity, setSelectedAssetsData, resetAssetsPendingCustodyAccount, setAssetsPendingCustodyAccount } from "domains/liquidity/store/liquidity.reducer";
import { useClientsSelector } from "domains/clients";
import { useTrustbenSelector } from "domains/trustben";
import { useAppDispatch } from "store/store.hooks";
import { liquidityApiReducer, fetchLRAssetsV2 } from "domains/liquidity/store/liquidity.api.reducer";
import { altQuotesApiReducer } from "domains/altQuotes/store/altQuotes.api.reducer";
import { authApiReducer } from "domains/authentication/store/auth.api.reducer";
import { useAssetsHoldingsData } from "domains/altCustody/components/AssetHoldingsTab/useAssetsHoldingsData";
import { useLiquiditySelector } from "domains/liquidity/store/liquidity.selectors";
import { useFullInvestments } from "common/shared-hooks/useFullInvestments";
import { useCustodyAccountsSelector } from "domains/custodyAccounts/store/custodyAccounts.selectors";
import { LAPages } from "domains/liquidity/shared/constants";
import { CustodyAccountStatus } from "domains/custodyAccounts/shared/types";

import "./RequestFormalProposalModal.scss";

interface IProps {
  isFormalProposalRequested?: boolean;
  isVisible: boolean;
  handleCancel: () => void;
}

const RequestFormalProposalModal: FC<IProps> = ({ isFormalProposalRequested, isVisible, handleCancel }) => {
  const navigate = useNavigate();
  const { t } = useTranslation([ "altQuotes", "common" ]);
  const dispatch = useAppDispatch();
  const { LRId: id } = useParams();
  const { isInvestmentInfoCompleted, navigateToEditAsset, setInvestmentInfoCompleted } = useFullInvestments();
  const [ updateLiquidityRequest ] = liquidityApiReducer.useUpdateLiquidityRequestMutation();
  const { liquidity_request_id: LRId } = useLiquiditySelector();
  const { investments } = useAssetsHoldingsData();
   
  const { data: liquidityRequests } = liquidityApiReducer.useFetchLiquidityRequestsQuery();
  const { isAdvisor } = useClientsSelector();
  const { isTrustbenAltQuote } = useTrustbenSelector();
  const { custodyAccounts } = useCustodyAccountsSelector();
  
  const activeCustodyAccounts = custodyAccounts?.filter(
    ( account: any ) => 
      account.account_status !== CustodyAccountStatus.Closed &&
      account.account_status !== CustodyAccountStatus.ClosedForNewAccount &&
      account.account_status !== CustodyAccountStatus.Duplicate,
  );

  const [
    _,
    { isLoading: isConvertingAltQuote },
  ] = altQuotesApiReducer.useConvertAltQuoteToLiquidityApplicationMutation({
    fixedCacheKey: "convert-altquote-to-liquidity-application",
  });
  const [ logout ] = authApiReducer.useLogoutMutation();

  const [ isCheckboxChecked, setIsCheckboxChecked ] = useState(false);
  const [ inputValue, setInputValue ] = useState("");
  const [ isLoading, setIsLoading ] = useState(false);
  const [ funds, setFunds ] = useState<any>([]);

  const liquidityRequest = liquidityRequests?.find((lr) => lr.id === LRId || lr.id === id );
  const { isFetching, LRAssets } = useLiquiditySelector()

  const fundAssets = inputValue
    ? funds.filter((fund:any) => {
        return fund?.asset_name.toLowerCase().includes(inputValue.toLowerCase());
      })
    : funds;
  const [ chosenAssets, setChosenAssets ] = useState<TAssetType[]>([]);
  const [ selectedRowKeys, setSelectedRowKeys ] = useState<string[]>([]);

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onSelect: (record: TConfirmAssetToIncludeItem) => {
      const selectedRowKeysCopy = [ ...selectedRowKeys ];
      if (selectedRowKeysCopy.indexOf(record.key) >= 0) {
        selectedRowKeysCopy.splice(selectedRowKeysCopy.indexOf(record.key), 1);
      } else {
        selectedRowKeysCopy.push(record.key);

        const selectedAsset = fundAssets.find((_: TAssetType, index:number) => +record.key === index);
        if (selectedAsset && !isInvestmentInfoCompleted(selectedAsset)) {
          navigateToEditAsset(selectedAsset.asset_id);
          return;
        }
      }

      const chosenAssets = setInvestmentInfoCompleted(fundAssets.filter((_:TAssetType, index:number) => selectedRowKeysCopy.some((key: string) => +key === index)));
      setChosenAssets(chosenAssets);
      setSelectedRowKeys(selectedRowKeysCopy);
    },
    onSelectAll: (selected: boolean) => {
      if (selected) {
        const allAssets: TAssetType[] = Object.values(fundAssets);
        allAssets.forEach((asset) => {
          if (!isInvestmentInfoCompleted(asset)) {
            navigateToEditAsset(asset.asset_id);
            return;
          }
        });
        setSelectedRowKeys(Object.keys(fundAssets));
        const chosenAssets = setInvestmentInfoCompleted(allAssets);
        setChosenAssets(chosenAssets);
      } else {
        setSelectedRowKeys([]);
        setChosenAssets([]);
      }
    },
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setIsCheckboxChecked(e.target.checked);
  };

  const handleContinueModal = async () => {
    setIsLoading(true)
    if (!liquidityRequest) {
      return;
    }

    if (isTrustbenAltQuote && LRId) {
      try {
        await updateLiquidityRequest({ 
          LRId, 
          changes: { 
            status: LiquidityRequestStatus.AltQuoteSubmitted,
          },
        });
        logout();
        navigate(URLs.AUTH.SIGN_IN, { state: { LRId }, replace: true });
      } catch (error) {
        console.error('Error updating liquidity request:', error);
        setIsLoading(false);
      }
      return;
    }

    const selectedAssets = chosenAssets.map((asset: TAssetType) => {
      const investment = investments?.find(({ asset_id: inv_asset_id }) => asset.asset_id === inv_asset_id )
      const result = { ...investment, is_investment_info_complete: asset.is_investment_info_complete };
      return result;
    });

    const assetIds = selectedAssets.map((inv) => inv?.id ?? '');

    dispatch(setSelectedAssetsForLiquidity(assetIds));
    dispatch(setSelectedAssetsData(selectedAssets as TAssetType[]));
    
    if (!isAdvisor) {
      if (!custodyAccounts || !activeCustodyAccounts?.length) {
        dispatch(resetAssetsPendingCustodyAccount())
        for (const asset of selectedAssets) {
          dispatch(setAssetsPendingCustodyAccount(asset.asset_id))
        }
        const custodyUrl = URLs.PROTECTED.MY_APPLICATIONS + `new-liquidity-request/${ LAPages.CustodyAccountAgreement }`;
        const reasonParam = liquidityRequest.reason_for_seeking_liquidity ? `?reason=${ liquidityRequest.reason_for_seeking_liquidity }` : '';
        navigate(custodyUrl + reasonParam, { state: { fromLiquidityRequestWithExpiredCAs: selectedAssets.map(({ asset_id } : any) => asset_id), reasonForSeekingLiquidity: liquidityRequest.reason_for_seeking_liquidity } });
        setIsLoading(false);
        return;
      }
    }
    
    const baseUrl = URLs.PROTECTED.MY_APPLICATIONS + `new-liquidity-request?confirmAssets=true`;
    const reasonParam = liquidityRequest.reason_for_seeking_liquidity ? `&reason=${ liquidityRequest.reason_for_seeking_liquidity }` : '';
    
    if (isCheckboxChecked) {
      const finalUrl = baseUrl + reasonParam;
      navigate(finalUrl, { state: { fromProposal: true } });
    } else {
      const finalUrl = baseUrl + '&submit=true' + reasonParam;
      navigate(finalUrl, { state: { fromProposal: true } });
    }
    
    setIsLoading(false)
  };

  const assetsToIncludeColumns = [
    {
      title: t("Request Formal Proposal Modal.Table Columns.0"),
      dataIndex: "assetName",
      key: "assetName",
      width: "64.5%",
      align: "left",
    } as const,
    {
      title: t("Request Formal Proposal Modal.Table Columns.1"),
      dataIndex: "valueInMyPortfolio",
      key: "valueInMyPortfolio",
      width: "30%",
      align: "right",
    } as const,
  ];

  const fundDataSource = fundAssets?.map((asset:any, i:any) => {
    const { asset_name, current_net_asset_value } = asset;
    return {
      key: String(i),
      assetName: asset_name,
      valueInMyPortfolio: formatCurrencyWithoutFractional(current_net_asset_value),
    };
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleCleanInput = () => {
    setInputValue("");
  };

  useEffect(() => { 
    if( !funds.length && isVisible ) { 
      dispatch(fetchLRAssetsV2( LRId || id )) 
    }
  }, [ isVisible ]);

  useEffect(() => {
    setFunds(LRAssets)
    setSelectedRowKeys(Object.keys(LRAssets));
    setChosenAssets(Object.values(LRAssets));
  }, [ LRAssets ]);

 
  return (
    <Modal
        afterClose={ () => setInputValue("") }
        className="ben-modal-request-formal-proposal"
        open={ isConvertingAltQuote || isVisible }
        width={ 725 }
        onCancel={ handleCancel }
        onOk={ handleContinueModal }
        footer={ [
          <Button disabled={ isConvertingAltQuote || isLoading } onClick={ handleCancel } key="back">
            {t("Cancel", { ns: "common" })}
          </Button>,
          <Button disabled={ isConvertingAltQuote || isLoading } onClick={ handleContinueModal } type="primary" key="continue">
            {t("Continue", { ns: "common" })}
          </Button>,
        ] }
    >
          <Spinner spinning={ isLoading }>
            <div className="ben-modal-request-formal-proposal-table">
              <h1>
                {isAdvisor ? t("Request Formal Proposal Modal.Advisor Title") : t("Request Formal Proposal Modal.User Title")}
              </h1>
              {isAdvisor ? (
                <Input
                  maxLength={ maxLengthText }
                  value={ inputValue }
                  addonBefore={ "Search" }
                  name="client_name"
                  className="ben-small ben-search ben-modal-request-formal-proposal-input"
                  onChange={ handleChange }
                  suffix={ inputValue ? <CircleX onClick={ handleCleanInput } /> : <span /> }
                />
              ) : (
                <p>
                  {t("Request Formal Proposal Modal.Text.0")}
                  <br />
                  {t("Request Formal Proposal Modal.Text.1")}
                </p>
              )}
              <Table
                loading={ isConvertingAltQuote || (isFetching ? isFetching : false) }
                rowSelection={ { type: "checkbox", ...rowSelection } }
                columns={ assetsToIncludeColumns }
                dataSource={ fundDataSource }
                pagination={ false }
                footer={ () => (
                  <Checkbox className="ben-modal-request-formal-proposal-table-checkbox" onChange={ onCheckboxChange }>
                    {t("Request Formal Proposal Modal.Checkbox Text")}
                  </Checkbox>
                ) }
              >
              </Table>
            </div>
          </Spinner>

    </Modal>
  );
};

export default RequestFormalProposalModal;
